import React, { useEffect, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'

import usePLPAnalytics from '@/hooks/usePLPAnalytics'
import { useClientSideCartAndUser, SiteWideSSRProps } from '@/services/getSiteWideInitialProps'

import type { CategoryPageBannerContentfulResponse, QueryParamsType } from '@/types'
import type { NextPage } from 'next'
import type { CategoryESresponse, BreadcrumbsType, FacetsType } from '@/types/ElasticSearch/SearchAPIResponse'
import type { MinPLPProductInfo } from '@/types/PLPProducts'
import { log } from '@/services/Log'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { BRAND } from '@/data/constants/BRAND'
import { getBloomreachConfig } from '@/helpers/getBloomreachConfig'
import { timePromiseAndLog } from '@/helpers/timePromiseAndLog'
import { OPENSERARCH_EXPERIMENT_ID, SHOP_ORIGIN } from '@/services/Configuration'
import Head from 'next/head'
import { getServerSideAbExperiment, useServerSideAbExperiment } from '@/helpers/getServerSideAbExperiment'

const Default = dynamic(import('@/layouts/default'))
const PLP = dynamic(import('@/components/Plp/PLP'))
interface BrandPageProps extends SiteWideSSRProps {
  legacyId: string,
  slug: string,
  query: QueryParamsType,
  path: string,
  currentPage: number,
  content: CategoryPageBannerContentfulResponse | null,
  sort?: string,
  userAgent: string,
  asPath: string | undefined,
  total?: number | undefined,
  category?: CategoryESresponse | null | undefined,
  products?: readonly MinPLPProductInfo[] | undefined,
  facets?: FacetsType[] | undefined,
  breadcrumbs?: BreadcrumbsType[] | undefined,
  itemsPerPage?: number | undefined,
  serverSideOpenSearchAB?: boolean,
}

const BrandPage: NextPage<BrandPageProps> = ({
  content,
  legacyId,
  slug,
  query,
  currentPage,
  sitewideBanner,
  megaNav,
  userAgent,
  products,
  breadcrumbs,
  category,
  facets,
  total,
  itemsPerPage,
  eventBanner,
  footer,
  sort,
  serverSideOpenSearchAB,
}) => {
  useServerSideAbExperiment({
    ctx: undefined,
    cookieName: 'openSearchAB',
    cookieFeature: 'isOpenSearchABEnabled',
    experimentId: OPENSERARCH_EXPERIMENT_ID,
    serverSideValue: serverSideOpenSearchAB,
  })
  const [isLoading, setIsLoading] = useState(false)
  const {
    cart, setCart, user, reloadCustomer,
  } = useClientSideCartAndUser()

  const categoryName = category?.name || ''
  const { PLPAnalytics } = usePLPAnalytics({
    plpType: 'brand',
    cart,
    customer: user,
    products,
    categoryName,
  })

  useEffect(() => {
    PLPAnalytics()
  }, [products, cart, PLPAnalytics])

  const metaTags = useMemo(() => {
    let description: string
    if (!breadcrumbs || breadcrumbs?.length === 0) {
      description = ''
    } else {
      description = breadcrumbs.slice(0).reverse().map(({ name }) => name).concat(BRAND)
        .join(' | ')
    }
    const title = `${categoryName} | Brands | One Kings Lane`
    return { description, title }
  }, [breadcrumbs, categoryName])

  const baseHref = `/brand?slug=${slug}&legacyId=${legacyId}&`
  const baseUrl = typeof window !== 'undefined' ? `${window.location.pathname}?` : ''

  return (
    <Default
      shouldUsePageView={false}
      reloadCustomer={reloadCustomer}
      user={user}
      sitewideBanner={sitewideBanner}
      megaNav={megaNav}
      meta={metaTags}
      cart={cart}
      onUpdateCart={setCart}
      eventBanner={eventBanner}
      maybeFooterFromContentful={footer}
    >
      <Head>
        <link rel="canonical" href={`${SHOP_ORIGIN}${String(category?.url)}`} />
      </Head>
      <PLP
        user={user}
        reloadCustomer={reloadCustomer}
        itemsPerPage={itemsPerPage}
        baseHref={baseHref}
        baseUrl={baseUrl}
        currentPage={currentPage}
        products={products}
        showBreadcrumbs={!(content?.hideBreadcrumbs)}
        breadcrumbs={breadcrumbs}
        PLPHeader={categoryName}
        totalNumberOfProducts={total}
        facets={facets}
        queryParam={query}
        plpBanner={content}
        userAgent={userAgent}
        isCategoryEvent
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        sortValue={sort}
        plpSource="brand"
        plpSourceId={legacyId}
      />
    </Default>
  )
}

const isNotBrandFacet = (facet: { name: string }) => facet.name?.toLocaleLowerCase() !== 'brand'

BrandPage.getInitialProps = async (ctx) => {
  const {
    req, res, query, asPath,
  } = ctx
  let userAgent = ''
  const legacyId = Array.isArray(query?.legacyId) ? query?.legacyId?.[0] : (query?.legacyId || '')
  const { slug, ...queryWithoutSlug } = query
  const path = `${asPath || ''}`.replace(/^(\/c-[a-z-]+)\//, '$1')
  log(`Category Page getServerSideProps ${JSON.stringify({ path, asPath })}`)
  const currentPage = parseInt(`${String(query?.page || '')}`) || 1
  try {
    const [
      { Content },
      { getSiteWideInitialProps },
      { getWindowAgent },
      { getRefererString },
      { fetchAndProcessCategoryPageProducts },
      { getQueryXssEscaped },
    ] = await allPromisesWithRetries(() => [
      import('@/services/Content'),
      import('@/services/getSiteWideInitialProps'),
      import('@/helpers/common'),
      import('@/helpers/getRefererString'),
      import('@/hooks/useSearchPageProducts'),
      import('@/helpers/getQueryWithValidCharacters'),
    ])
    const legalQuery = getQueryXssEscaped(queryWithoutSlug)
    userAgent = getWindowAgent(req) || userAgent
    const id = `brand${legalQuery?.id || legalQuery?.legacyId || ''}`

    const serverSideOpenSearchAB = getServerSideAbExperiment({
      ctx: { req, res },
      cookieName: 'openSearchAB',
      cookieFeature: 'isOpenSearchABEnabled',
      experimentId: OPENSERARCH_EXPERIMENT_ID,
    })

    const [
      siteWideInitialProps,
      content,
      searchResponse,
    ] = await Promise.all([
      timePromiseAndLog('BrandPage.getInitialProps.getSiteWideInitialProps')(getSiteWideInitialProps(ctx, serverSideOpenSearchAB)),
      timePromiseAndLog('BrandPage.getInitialProps.Content.categoryPage')(Content.categoryPage({ id: legacyId })),
      timePromiseAndLog('BrandPage.getInitialProps.fetchAndProcessCategoryPageProducts')(fetchAndProcessCategoryPageProducts({
        asPath: String(asPath || ''),
        referer: getRefererString(req?.headers?.referer),
        bloomreachConfig: getBloomreachConfig(ctx, serverSideOpenSearchAB),
        query: {
          ...legalQuery,
          id,
          legacyId: id,
        },
      })),
      /* */
    ])
    const {
      products,
      breadcrumbs,
      category,
      facets: responseFacets,
      total,
      itemsPerPage,
      abScheduleEnabled,
    } = searchResponse ?? {
      abScheduleEnabled: false,
    }
    if (abScheduleEnabled) {
      res?.setHeader?.('cache-control', 'public, must-revalidate, proxy-revalidate, max-age=0, s-maxage=0')
    }
    if (!products?.length) {
      res?.writeHead(302, {
        Location: `/c/shop-sale?redirectFrom=${encodeURIComponent(asPath || `/b-${legacyId}`)}`,
        'cache-control': 'public, must-revalidate, proxy-revalidate, max-age=0, s-maxage=0',
      })
      res?.end()
      log(`No products found, sleeping for ${15}s`)
      await new Promise((resolve) => setTimeout(resolve, 15000))
    }
    const facets = (responseFacets || []).filter(isNotBrandFacet)
    return {
      legacyId,
      slug: (slug as string),
      content,
      query: legalQuery,
      ...siteWideInitialProps,
      sort: legalQuery?.sort,
      path,
      currentPage,
      userAgent,
      asPath,
      products,
      breadcrumbs,
      category,
      facets,
      total,
      itemsPerPage,
      serverSideOpenSearchAB,
    }
  } catch (e) {
    log('Cannot get initial props in category page, redirecting', e)
    res?.writeHead(302, {
      Location: '/',
      'cache-control': 'public, must-revalidate, proxy-revalidate, max-age=0, s-maxage=0',
    })
    res?.end()
  }
  const [{ SiteWideInitialPropsFailure }] = await allPromisesWithRetries(() => [import('@/services/getSiteWideInitialProps')])
  return {
    legacyId,
    slug: (slug as string),
    total: 0,
    content: null,
    query: {},
    path,
    currentPage,
    category: null,
    products: [],
    facets: [],
    breadcrumbs: [],
    itemsPerPage: 60,
    ...SiteWideInitialPropsFailure,
    userAgent,
    asPath,
  }
}

export default BrandPage
